import React, { useEffect, useState } from 'react';

import { FileExcelOutlined } from '@ant-design/icons';
import {
  Button,
  Col,
  DatePicker,
  Divider,
  Modal,
  Row,
  Typography,
  message,
} from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import {
  DiReportService,
  GetCsvConsolidatedRequestDto,
} from 'services/DiReportService';

import appI18n from '../../../../config/i18n';

interface DownloadCsvModalProps {
  isOpen: boolean;
  handleModalCancel: () => void;
}

function DownloadCsvModal({
  isOpen,
  handleModalCancel,
}: DownloadCsvModalProps) {
  const { Text } = Typography;
  const { RangePicker } = DatePicker;
  const [loading, setLoading] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const { t } = useTranslation(undefined, { i18n: appI18n });
  const dateFormat: string = useSelector(
    (state: any) => state.intl?.dateFormat
  );

  // Estado para el rango de fechas
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(
    null
  );

  // Resetear el rango de fechas cuando se abre el modal
  useEffect(() => {
    if (isOpen) {
      setDateRange(null);
    }
  }, [isOpen]);

  // Determinar si el botón de enviar debe estar deshabilitado
  const isSubmitDisabled = !dateRange;

  const handleDownloadCsv = async () => {
    if (!dateRange) {
      messageApi.error(
        t(
          'scenes.data_ingester.data_query.components.download_csv_modal.errors.select_date_range'
        )
      );
      return;
    }

    setLoading(true);
    try {
      const messageKey = 'downloading';
      messageApi.loading({
        content: t(
          'scenes.data_ingester.data_query.components.download_csv_modal.messages.downloading_file'
        ),
        key: messageKey,
        duration: 0,
      });

      const requestBody: GetCsvConsolidatedRequestDto = {
        startDate: dateRange[0].format('YYYY-MM-DD'),
        endDate: dateRange[1].format('YYYY-MM-DD'),
      };

      const response = await DiReportService.getCsvConsolidated(requestBody);

      if (response.success && response.data) {
        const data = response.data as { blob: Blob; filename: string };

        // Crear un enlace temporal para la descarga
        const url = window.URL.createObjectURL(data.blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = data.filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        // Destruir mensaje y mostrar éxito
        messageApi.destroy(messageKey);
        messageApi.success(
          t(
            'scenes.data_ingester.data_query.components.download_csv_modal.messages.download_success'
          )
        );

        // Cerrar el modal
        handleModalCancel();
      } else {
        messageApi.error({
          content: t(
            'scenes.data_ingester.data_query.components.download_csv_modal.errors.download_file'
          ),
          key: messageKey,
        });
      }
    } catch (error) {
      console.error('Error al descargar el archivo:', error);
      messageApi.error(
        t(
          'scenes.data_ingester.data_query.components.download_csv_modal.errors.download_file'
        )
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <span>
          <FileExcelOutlined />{' '}
          {t(
            'scenes.data_ingester.data_query.components.download_csv_modal.title'
          )}
        </span>
      }
      open={isOpen}
      onCancel={handleModalCancel}
      maskClosable={false}
      destroyOnClose
      footer={null}
      width={500}
    >
      {contextHolder}
      <Divider style={{ marginTop: '15px', marginBottom: '15px' }} />

      <Row gutter={[16, 16]}>
        <Col xs={24}>
          <Text strong style={{ display: 'block', marginBottom: '8px' }}>
            {t(
              'scenes.data_ingester.data_query.components.download_csv_modal.labels.date_range'
            )}{' '}
            <span style={{ color: '#ff4d4f' }}>*</span>
          </Text>
          <RangePicker
            className="date-range-picker"
            style={{ width: '100%' }}
            format={dateFormat}
            value={dateRange}
            onChange={(dates) => {
              if (dates) {
                setDateRange([
                  dates[0] as dayjs.Dayjs,
                  dates[1] as dayjs.Dayjs,
                ]);
              } else {
                setDateRange(null);
              }
            }}
            placeholder={[
              t(
                'scenes.data_ingester.data_query.components.download_csv_modal.placeholders.start_date'
              ),
              t(
                'scenes.data_ingester.data_query.components.download_csv_modal.placeholders.end_date'
              ),
            ]}
          />
          <Text
            type="secondary"
            style={{ display: 'block', marginTop: '4px', fontSize: '12px' }}
          >
            {t(
              'scenes.data_ingester.data_query.components.download_csv_modal.helper.select_range_to_export'
            )}
          </Text>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: '24px' }}>
        <Col xs={24}>
          <Button
            type="primary"
            block
            onClick={handleDownloadCsv}
            loading={loading}
            disabled={isSubmitDisabled}
          >
            {t(
              'scenes.data_ingester.data_query.components.download_csv_modal.buttons.download'
            )}
          </Button>
        </Col>
      </Row>
    </Modal>
  );
}

export default DownloadCsvModal;
